import supabase from '@/config/supabase'

export type BroadcastEvent =
  | 'player_ready_toggled'
  | 'host_start_game'
  | 'round_started'
  | 'vote_cast'
  | 'vote_preview'
  | 'player_joined'
  | 'player_left'
  | 'prompt_locked'
  | 'prompt_submitted'
  | 'word_submitted'
  | 'voting_prompt_started'
  | 'next_round_started'
  | 'all_rounds_completed'
  | 'player_kicked'

export interface PresenceUserMeta {
  id: string
  name: string
  isHost: boolean
  isReady: boolean
}

export interface RoundStartedPayload {
  roundNumber: number
  phase: 'prompt_writing' | 'voting'
  startedAt: number // Date.now()
  timeLimitSec: number
  word?: string // Only for prompt_writing phase
}

class RealtimeService {
  private channels: Map<string, ReturnType<typeof supabase.channel>> = new Map()

  private getChannel(roomId: string) {
    const key = `room:${roomId}`
    let ch = this.channels.get(key)
    if (!ch) {
      // Create channel with more specific configuration
      ch = supabase.channel(key, {
        config: {
          broadcast: { self: true },
          presence: { key: '' }
        }
      })
      this.channels.set(key, ch)
    }
    return ch
  }

  // Clean up a specific room's channel completely
  async cleanupRoom(roomId: string) {
    const key = `room:${roomId}`
    const ch = this.channels.get(key)
    if (ch) {
      try {
        await ch.untrack()
      } catch (e) {
        console.log('Untrack error (expected):', e)
      }
      try {
        await ch.unsubscribe()
      } catch (e) {
        console.log('Unsubscribe error (expected):', e)
      }
      try {
        await supabase.removeChannel(ch)
      } catch (e) {
        console.log('Remove channel error (expected):', e)
      }
      this.channels.delete(key)
      console.log(`Cleaned up room channel: ${roomId}`)
    }
  }

  async joinRoom(roomId: string, user: PresenceUserMeta) {
    const ch = this.getChannel(roomId)
    
    // Subscribe and track user
    await ch.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await ch.track(user)
      }
    })

    return ch
  }

  async leaveRoom(roomId: string) {
    await this.cleanupRoom(roomId)
  }

  onPresenceSync(roomId: string, cb: (users: PresenceUserMeta[]) => void) {
    const ch = this.getChannel(roomId)
    const handler = () => {
      const state = ch.presenceState() as Record<string, Array<PresenceUserMeta>>
      const users: PresenceUserMeta[] = []
      Object.values(state).forEach((metas) => {
        metas.forEach((m) => users.push(m))
      })
      cb(users)
    }

    ch.on('presence', { event: 'sync' }, handler)
    ch.on('presence', { event: 'join' }, handler)
    ch.on('presence', { event: 'leave' }, handler)
    return () => {
      // No specific off API; removing channel cleans up
    }
  }

  on(roomId: string, event: BroadcastEvent, cb: (payload: any) => void) {
    const ch = this.getChannel(roomId)
    ch.on('broadcast', { event }, ({ payload }) => cb(payload))
    return () => {
      // No specific off API; removing channel cleans up
    }
  }

  async broadcast(roomId: string, event: BroadcastEvent, payload: any) {
    const ch = this.getChannel(roomId)
    await ch.send({ type: 'broadcast', event, payload })
  }
}

export const realtimeService = new RealtimeService()
export default realtimeService


